import {Table} from "antd";
import type {ColumnsType, TableProps as AntTableProps} from "antd/es/table";
import React from "react";
import {IPageInfo} from "../../redux/types";
import {withSorters} from "./sorting";
import {COLORS} from "../../designSystem";

interface TableProps<T> {
    columns: ColumnsType<T>;
    data: T[] | null;
    pageInfo: IPageInfo;
    handlePageChange?: (pageNumber: number) => void;
    loading?: boolean;
    /** Row expansion — used to open a platform's sub-merchants in place. */
    expandable?: AntTableProps<any>["expandable"];
}

export const GenericTable: React.FC<TableProps<any>> = ({
    columns,
    data,
    pageInfo,
    handlePageChange,
    loading = false,
    expandable,
}) => {
    const tableChangeHandler = (pagination: any, filters: any, sorter: any) => {
        if (pagination?.current !== pageInfo.pageNumber) {
            if (handlePageChange) {
                handlePageChange(pagination.current)
            } else {
                console.error(`handlePageChange function is not provided. Current page number is not being handled. ${pagination.current}`);
            }
        }
    }

    const rows = data || [];
    // Every column with a field becomes sortable here, so the whole app gains sorting without
    // each page declaring it. A column opts out with `sortAs: false`.
    const sortableColumns = React.useMemo(
        () => withSorters(columns as any[], rows) as ColumnsType<any>,
        [columns, rows],
    );
    const pagedServerSide = (pageInfo?.totalPages || 0) > 1;

    return (
        <div className="mt-8">
            <Table
                rowKey="key"
                loading={loading}
                scroll={{x: "100%"}}
                columns={sortableColumns}
                dataSource={rows}
                expandable={expandable}
                pagination={{
                    total: pageInfo?.totalItems || 0,
                    current: pageInfo?.pageNumber || 1,
                    pageSize: pageInfo?.pageSize || 10,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                }}
                onChange={tableChangeHandler}
            />
            {/*
              Rows are fetched a page at a time, so sorting orders this page rather than the
              whole result set. Saying so beats letting a reader assume the top row is the
              largest of everything.
            */}
            {pagedServerSide && (
                <p className="mt-2 mb-0 text-xs" style={{color: COLORS.textMuted}}>
                    Sorting orders the {rows.length} rows on this page. Narrow the filters to sort across every result.
                </p>
            )}
        </div>
    );
};
